import { StrictMode } from 'react'
import { preconnect, prefetchDNS, preload } from 'react-dom'
import ReactDOM from 'react-dom/client'

// Import Poppins font and ship at build time
// Get font URL for preloading
import poppinsWoff2 from '@fontsource/poppins/files/poppins-latin-400-normal.woff2?url'

import '@fontsource-variable/jetbrains-mono'
import '@fontsource/poppins'

// App Component
import App from './app'
// Import global styles
import { API_URL, isMockingEnabled } from './lib/constants'

import './index.css'

import * as Sentry from '@sentry/react'

import { env } from './env'
import { Providers } from './providers'

// A deploy swaps index.html under a tab that still references the old hashed
// chunks. S3 keeps old /assets around (terraform/build.tf sync pass 1) so this
// should be rare — but if a lazy chunk is truly gone, reload once to pick up
// the fresh index.html instead of white-screening on a failed dynamic import.
window.addEventListener('vite:preloadError', (event) => {
	event.preventDefault()
	window.location.reload()
})

// Preload API url for faster loading
preconnect(API_URL)
prefetchDNS(API_URL)

// Preconnect to Clerk
preconnect(env.VITE_CLERK_PRECONNECT_URL)

// Preconnect to PostHog
preconnect(env.VITE_POSTHOG_HOST)

// Preload Poppins font for critical rendering path
preload(poppinsWoff2, {
	as: 'font',
	type: 'font/woff2',
	crossOrigin: 'anonymous',
})

/** Starts the MSW worker only when mocking (dev:mock or demo build — ADR 0006 decision 1); gates the app bootstrap so requests are intercepted before render. */
async function enableMocking() {
	// If not in a mocked mode, do not enable mocking
	if (!isMockingEnabled) return

	// Build-time literal (folded in vite.config `define`, same mechanism as
	// VITE_CAPTURE): the runtime `isMockingEnabled` gate above can't be
	// statically evaluated (it flows through the zod env parse), so without
	// this Rollup emits the whole mock chunk — world, faker, MSW — into EVERY
	// build's dist/. A constant-false `if` is a dead branch Rollup provably
	// drops (the VITE_CAPTURE shape in `@/lib/api`), removing the import from
	// non-mock production builds; `scripts/scan-bundle.js` is the regression
	// gate (C2 — knowledge/adr/0007-research/refine-review-findings.md).
	if (import.meta.env.VITE_INCLUDE_MOCKS !== 'true') return

	// The fail-loud `onUnhandledRequest` policy and the pass-through allowlist it
	// consults live in that module, not here: they are mock-layer detail, and a
	// module this entry imported eagerly would drag `__mocks__` into every
	// production bundle. Start there to change what the offline app may reach.
	const { startMockWorker } = await import('./__mocks__/browser')

	return startMockWorker()
}

/**
 * Main app rendering function
 */
function renderApp() {
	const rootElement = document.getElementById('root')
	if (rootElement && !rootElement.innerHTML) {
		const root = ReactDOM.createRoot(rootElement, {
			// Callback called when an error is thrown and not caught by an ErrorBoundary.
			onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
				console.warn('Uncaught error', error, errorInfo.componentStack)
			}),
			// Callback called when React catches an error in an ErrorBoundary.
			onCaughtError: Sentry.reactErrorHandler(),
			// Callback called when React automatically recovers from errors.
			onRecoverableError: Sentry.reactErrorHandler(),
		})
		root.render(
			<StrictMode>
				<Providers>
					<App />
				</Providers>
			</StrictMode>,
		)

		// Remove initial HTML loader now that React has mounted
		document.getElementById('initial-loader')?.remove()
	}
}

// Start the app after checking if we should mock
if (isMockingEnabled) {
	enableMocking().then(renderApp)
} else {
	renderApp()
}
